import React, { FC, useState } from 'react';
import { Box, Button, Divider, Typography, useTheme } from '@mui/material';
import {
  ExpandLess,
  MoreVert,
  SentimentVeryDissatisfied,
  SentimentVerySatisfied,
} from '@mui/icons-material';
import { IReviewNew, IReviewNewProps } from '../../../shared/interfaces/IReviewNew';
import ReviewWrapper from '../ReviewWrapper';
import ReviewHeader from '../ReviewHeader';
import ReviewContent from '../ReviewContent';
import ReviewFooter from '../ReviewFooter';
import { useTranslation } from 'react-i18next';
import Block from '../../Block/Block';
import { CommunityManagement } from '../../../shared/interfaces/ICommunityManagement';
import Status from '../../Status/Status';
import OpinionIndicator from '../../OpinionIndicator';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

interface Props extends IReviewNewProps {}

const AutoscoutReview: FC<Props> = ({
  communityManagement,
  commentingModule,
  hideHeader,
  hideFooter,
  statusModule,
  status,
  deletionModule,
  reviewEntry,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [showAllBlocks, setShowAllBlocks] = useState(false);

  const {
    header,
    username,
    positionText,
    rating,
    companyComment,
    blocks,
    negativeFeedback,
    positiveFeedback,
    text,
  } = reviewEntry.review;

  return (
    <ReviewWrapper
      communityManagement={communityManagement}
      commentingModule={commentingModule}
      hideHeader={hideHeader}
      hideFooter={hideFooter}
      rating={rating}
      statusModule={statusModule}
      status={status}
      deletionModule={deletionModule}
      reviewEntry={reviewEntry}
    >
      {(username || positionText) && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {username && (
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
              }}
            >
              {username}
            </Typography>
          )}
          {positionText && (
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                cursor: 'pointer',
              }}
            >
              {positionText}
            </Typography>
          )}
        </Box>
      )}
      {header && (
        <Typography
          variant="h6"
          sx={{
            mt: theme.spacing(2),
          }}
        >
          {header}
        </Typography>
      )}
      {text && (
        <Typography
          variant="body1"
          sx={{
            mt: theme.spacing(2),
          }}
        >
          {text}
        </Typography>
      )}
      {positiveFeedback && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mt: theme.spacing(1),
            ml: theme.spacing(1),
          }}
        >
          <SentimentVerySatisfied sx={{ color: theme.palette.success.dark }} />
          <Typography sx={{ ml: theme.spacing(3) }}>{positiveFeedback}</Typography>
        </Box>
      )}
      {negativeFeedback && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mt: theme.spacing(1),
            ml: theme.spacing(1),
          }}
        >
          <SentimentVeryDissatisfied sx={{ color: theme.palette.error.dark }} />
          <Typography sx={{ ml: theme.spacing(3) }}>{negativeFeedback}</Typography>
          <Divider
            orientation="horizontal"
            sx={{ mt: theme.spacing(1) }}
          />
        </Box>
      )}
      {blocks !== undefined && blocks.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            mt: theme.spacing(3),
            ml: theme.spacing(7),
            mb: theme.spacing(3),
          }}
        >
          {blocks
            .filter((value) => typeof value.rating === 'string')
            .filter((value) => value.title !== null)
            .map(({ rating, title }, index) => {
              return (
                <Box
                  key={`Review_${reviewEntry.id}_Block_${index}`}
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'flex-end',
                  }}
                >
                  {rating === 'YES' ? (
                    <>
                      <CheckIcon sx={{ color: theme.palette.success.dark }} />
                      <Status
                        key={`Review_${reviewEntry.id}_Block_${index}`}
                        text={title}
                        iconBefore={<OpinionIndicator rating={rating} />}
                        sx={{ mx: theme.spacing(1) }}
                      />
                    </>
                  ) : (
                    <>
                      <CloseIcon sx={{ color: theme.palette.error.dark }} />
                      <Status
                        key={`Review_${reviewEntry.id}_Block_${index}`}
                        text={title}
                        iconBefore={<OpinionIndicator rating={rating} />}
                        sx={{ mx: theme.spacing(1) }}
                      />
                    </>
                  )}
                </Box>
              );
            })}
        </Box>
      )}
      {blocks && blocks.length > 0 && !showAllBlocks && (
        <Box sx={{ mt: theme.spacing(1) }}>
          <Divider orientation="horizontal" />
          <Block
            rating={blocks.filter((value) => typeof value.rating === 'number')[0].rating}
            text={blocks.filter((value) => typeof value.rating === 'number')[0].text}
            title={blocks.filter((value) => typeof value.rating === 'number')[0].title}
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<MoreVert />}
            onClick={() => setShowAllBlocks(!showAllBlocks)}
            sx={{
              my: theme.spacing(1),
            }}
          >
            {t('show_more')}
          </Button>
        </Box>
      )}
      {blocks && blocks.length > 0 && showAllBlocks && (
        <Box sx={{ mt: theme.spacing(3) }}>
          {blocks
            .filter((value) => typeof value.rating === 'number')
            .map(({ rating, text, title }, index) => {
              return (
                <Box
                  key={`Review_${reviewEntry.id}_Block_${index}`}
                  sx={{ mt: theme.spacing(1) }}
                >
                  <Divider orientation="horizontal" />
                  <Block
                    rating={rating}
                    text={text}
                    title={title}
                  />
                </Box>
              );
            })}
          <Button
            variant="outlined"
            size="small"
            startIcon={<ExpandLess />}
            onClick={() => setShowAllBlocks(!showAllBlocks)}
            sx={{
              mb: theme.spacing(1),
            }}
          >
            {t('show_less')}
          </Button>
        </Box>
      )}
    </ReviewWrapper>
  );
};

export default AutoscoutReview;
